/**
 * advance-weekly.ts — whether an advance is repaid by a weekly of its own.
 *
 * An advance with no weekly is SETTLED AT THE END OF THE CONTRACT: nothing separate is collected
 * while the vehicle is being paid for, and once it is paid for the contract instalment keeps
 * running until the advance is recovered. Three surfaces in this app have to know which of the two
 * an advance is — the Insurance tab's "Active advances weekly" stat, the badge and striped fill on
 * a policy card, and the same pair on an operational advance card — and they spelled the question
 * two different ways between them. It is answered here once instead. [D-B58]
 *
 * ── THE SCHEDULE ANSWERS IT, AND THE MONEY MAY NOT ───────────────────────────
 * Once the engine starts crediting these advances, `allocated_total` and `recent_allocations`
 * begin to move on exactly the advances that have NO weekly. A predicate that read either of them
 * would therefore have flipped its answer on the first credited pesewa: the "Settled at end of
 * contract" badge would vanish from the advances the arrangement is about, the striped fill would
 * go with it, and the stat card would start billing a synthetic `total / 52` that nobody is
 * charged. An instalment row is the only evidence that a weekly is being collected.
 *
 * ── THE LATEST ROW, AND WHY IT HAS TO BE POSITIVE ────────────────────────────
 * `get_contract_insights` builds the array `starts_at DESC` (`…_the_advance_says_what_it_was_for
 * .sql:310` for an operational advance, `:334` for a premium one) and leaves it NULL, never `[]`,
 * when there are no rows — so `[0]` is the latest row. A schedule whose latest row is zero is a
 * weekly that has been switched off, which is the same arrangement as never having had one. That
 * is the server's own test too: the allocator admits an advance to the weekly split on the amount
 * of its latest row alone, so reading anything else here would put this app and the engine on
 * opposite sides of one question.
 */

/** An advance's instalment schedule, to the depth this reads. Both kinds carry the same shape. */
export interface AdvanceScheduleShape {
    /** The advance's instalment rows, latest first. NULL — not `[]` — when it has none. */
    instalments?: readonly { amount?: number | null }[] | null;
}

/**
 * Whether this advance carries a weekly of its own.
 *
 * Fails closed on a payload that cannot answer: a missing schedule is not evidence of a weekly,
 * and "settled at the end of the contract" is the reading that cannot bill a driver for one.
 */
export function carriesWeekly(advance: AdvanceScheduleShape | null | undefined): boolean {
    const latest = advance?.instalments?.[0]?.amount;

    return typeof latest === 'number' && Number.isFinite(latest) && latest > 0;
}
